/**
 * LaGift — Gift Finder
 * ---------------------------------------------------------------------------
 * Three questions, then a shortlist. The scoring rules and why they are what
 * they are live in sections/lagift-gift-finder.liquid; the short version is
 * that tags add points and a budget removes products outright.
 *
 * The whole quiz is hidden in the markup and revealed here, so a browser with
 * no JavaScript sees the invitation and a link into the catalogue rather than
 * a set of questions that cannot be answered.
 */
/**
 * Scoped so the file can run more than once. A `<script src>` executes once per
 * tag, not once per URL, and a merchant can put the same section on a page
 * twice - a top-level class would then throw "Identifier ... has already been
 * declared" on the second run.
 */
(() => {
  /** Answers are carried in the URL so a result can be shared and Back works. */
  const PARAM = 'gf';

  class LagiftGiftFinder extends HTMLElement {
    connectedCallback() {
      this.intro = this.querySelector('[data-finder-intro]');
      this.quiz = this.querySelector('[data-finder-quiz]');
      this.results = this.querySelector('[data-finder-results]');
      this.grid = this.querySelector('[data-finder-grid]');
      this.empty = this.querySelector('[data-finder-empty]');
      this.rest = this.querySelector('[data-finder-rest]');
      this.progress = this.querySelector('[data-finder-progress]');
      this.steps = Array.from(this.querySelectorAll('[data-finder-step]'));
      this.products = Array.from(this.querySelectorAll('[data-finder-product]'));

      if (!this.steps.length || !this.results) return;

      this.resultsCount = parseInt(this.dataset.resultsCount, 10) || 6;

      /** One entry per question: an answer object, or null for skipped. */
      this.answers = new Array(this.steps.length).fill(null);
      this.index = 0;

      this.addEventListener('click', this.onClick.bind(this));
      window.addEventListener('popstate', this.onPopState.bind(this));

      /**
       * The start button is hidden in the markup and shown here. That is the
       * whole no-JavaScript story: without this line it stays hidden and the
       * section is an invitation with a browse link under it.
       */
      const start = this.querySelector('[data-finder-start]');
      if (start) start.hidden = false;

      this.restore();
    }

    onClick(event) {
      const answer = event.target.closest('[data-finder-answer]');
      if (answer && this.contains(answer)) return this.answer(answer);

      if (event.target.closest('[data-finder-start]')) return this.begin();
      if (event.target.closest('[data-finder-skip]')) return this.skip();
      if (event.target.closest('[data-finder-back]')) return this.back();
      if (event.target.closest('[data-finder-restart]')) return this.restart();
    }

    begin() {
      this.intro.hidden = true;
      this.results.hidden = true;
      this.quiz.hidden = false;
      this.show(0);
    }

    /** Records the answer on the step it belongs to, then moves on. */
    answer(button) {
      const step = button.closest('[data-finder-step]');
      const index = this.steps.indexOf(step);
      if (index === -1) return;

      const min = button.dataset.finderMin;
      const max = button.dataset.finderMax;

      this.answers[index] = button.hasAttribute('data-finder-min')
        ? { kind: 'budget', min: toNumber(min), max: toNumber(max), button }
        : { kind: 'tags', tags: splitTags(button.dataset.finderTags), button };

      for (const sibling of step.querySelectorAll('[data-finder-answer]')) {
        sibling.classList.toggle('is-chosen', sibling === button);
      }

      this.advance();
    }

    skip() {
      this.answers[this.index] = null;
      const step = this.steps[this.index];
      for (const sibling of step.querySelectorAll('[data-finder-answer]')) {
        sibling.classList.remove('is-chosen');
      }
      this.advance();
    }

    advance() {
      if (this.index < this.steps.length - 1) return this.show(this.index + 1);
      this.finish();
    }

    back() {
      if (this.index === 0) return;
      this.show(this.index - 1);
    }

    show(index) {
      this.index = index;

      this.steps.forEach((step, i) => {
        step.hidden = i !== index;
      });

      const back = this.steps[index].querySelector('[data-finder-back]');
      if (back) back.hidden = index === 0;

      if (this.progress) {
        this.progress.textContent = `${index + 1} / ${this.steps.length}`;
      }

      /**
       * Focus the question rather than the first answer. Landing on an answer
       * reads as though it were already chosen, and a screen reader would skip
       * the question it belongs to.
       */
      const question = this.steps[index].querySelector('.lagift-finder__question');
      if (question) {
        question.setAttribute('tabindex', '-1');
        question.focus({ preventScroll: true });
      }
    }

    finish(options = {}) {
      this.quiz.hidden = true;
      this.results.hidden = false;

      const chosen = this.answers.filter(Boolean);
      const budget = chosen.find((answer) => answer.kind === 'budget') || null;
      const tagAnswers = chosen.filter((answer) => answer.kind === 'tags');

      const scored = [];
      for (const product of this.products) {
        if (budget && !withinBudget(product, budget)) {
          product.hidden = true;
          continue;
        }

        const tags = splitTags(product.dataset.finderTags);
        let score = 0;
        for (const answer of tagAnswers) {
          if (answer.tags.some((tag) => tags.includes(tag))) score += 1;
        }
        scored.push({ product, score, available: product.dataset.finderAvailable !== '0' });
      }

      /**
       * Anything that matched an answer first, then in stock, then the closest.
       *
       * A match leads because the shortlist is presented in two groups and the
       * first one is the answer to what was asked; a match nobody can buy is
       * still a better answer than a product picked because it happened to be
       * in the right price band. Within each group buyable comes first, which
       * is what the single-unit catalogue needs: most things here are one of
       * one, so sold out is a normal state rather than an edge case.
       */
      scored.sort(
        (a, b) =>
          Number(b.score > 0) - Number(a.score > 0) ||
          Number(b.available) - Number(a.available) ||
          b.score - a.score
      );

      const winners = scored.slice(0, this.resultsCount);
      const matched = winners.filter((entry) => entry.score > 0);
      const rest = winners.filter((entry) => entry.score === 0);

      for (const { product } of scored) product.hidden = true;
      for (const { product } of matched) {
        product.hidden = false;
        this.grid.append(product);
      }

      /**
       * The shortlist is filled out with products that answered nothing, so
       * that a thin catalogue or a narrow budget never returns an empty page.
       * They are not allowed under a heading that says they suit her, so this
       * line goes between the two groups - only when there is something on
       * both sides of it, and only when a question was actually answered.
       */
      if (this.rest) {
        this.rest.hidden = !(matched.length && rest.length && tagAnswers.length);
        if (!this.rest.hidden) this.grid.append(this.rest);
      }

      for (const { product } of rest) {
        product.hidden = false;
        this.grid.append(product);
      }

      /**
       * Said whenever nothing matched an answer at all, whether or not there
       * are products underneath. A budget alone can leave a full grid that owes
       * nothing to the questions, and claiming those are a match would be a
       * lie; an empty grid with no explanation is worse.
       */
      if (this.empty) this.empty.hidden = !(matched.length === 0 && tagAnswers.length > 0);

      if (!options.silent) {
        this.push();
        this.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    restart() {
      this.answers.fill(null);
      for (const button of this.querySelectorAll('[data-finder-answer]')) {
        button.classList.remove('is-chosen');
      }
      for (const product of this.products) product.hidden = true;
      if (this.rest) this.rest.hidden = true;

      this.results.hidden = true;
      this.quiz.hidden = true;
      this.intro.hidden = false;
      this.index = 0;
      this.push();
    }

    /**
     * The answers as `gf=0,,2` - one slot per question, the chosen answer's
     * position, empty where it was skipped. Positions rather than tags so the
     * URL does not go stale the day the merchant renames a tag; a question that
     * has since lost an answer simply restores nothing for that slot.
     */
    serialise() {
      if (!this.answers.some(Boolean)) return '';
      return this.answers
        .map((answer, i) => {
          if (!answer) return '';
          const buttons = Array.from(this.steps[i].querySelectorAll('[data-finder-answer]'));
          return String(buttons.indexOf(answer.button));
        })
        .join(',');
    }

    push() {
      const url = new URL(window.location.href);
      const value = this.serialise();
      if (value) {
        url.searchParams.set(PARAM, value);
      } else {
        url.searchParams.delete(PARAM);
      }
      window.history.pushState({}, '', url);
    }

    onPopState() {
      this.restore();
    }

    /** Rebuilds a result from the URL, without pushing another entry onto it. */
    restore() {
      const value = new URL(window.location.href).searchParams.get(PARAM);
      if (!value) {
        this.answers.fill(null);
        for (const button of this.querySelectorAll('[data-finder-answer]')) {
          button.classList.remove('is-chosen');
        }
        for (const product of this.products) product.hidden = true;
        this.results.hidden = true;
        this.quiz.hidden = true;
        this.intro.hidden = false;
        return;
      }

      const positions = value.split(',');
      this.answers.fill(null);

      this.steps.forEach((step, i) => {
        const buttons = Array.from(step.querySelectorAll('[data-finder-answer]'));
        const position = parseInt(positions[i], 10);
        const button = Number.isNaN(position) ? null : buttons[position];
        for (const sibling of buttons) sibling.classList.toggle('is-chosen', sibling === button);
        if (!button) return;

        this.answers[i] = button.hasAttribute('data-finder-min')
          ? {
              kind: 'budget',
              min: toNumber(button.dataset.finderMin),
              max: toNumber(button.dataset.finderMax),
              button,
            }
          : { kind: 'tags', tags: splitTags(button.dataset.finderTags), button };
      });

      this.intro.hidden = true;
      this.finish({ silent: true });
    }
  }

  /** An empty bound is "no bound", not zero. "200-" is everything from 200 up. */
  function toNumber(value) {
    if (value === undefined || value === null || value.trim() === '') return null;
    const number = parseFloat(value);
    return Number.isNaN(number) ? null : number;
  }

  /**
   * Tags are matched as text, so anything invisible inside one is a mismatch
   * with nothing on screen to explain it.
   *
   * A tag copied out of a formatted message - our own reply to the merchant,
   * as it turned out - arrives carrying a narrow no-break space and a word
   * joiner: "style:minimal\u202f\u2060" is not "style:minimal". `trim` drops
   * the first, because a narrow no-break space is whitespace, and keeps the
   * second, because a word joiner is not. Three of the shop's tags were
   * written that way and simply never matched.
   *
   * So the format characters go first and the trim goes second. This runs on
   * both sides of the comparison, the product's tags and the answer's, because
   * either can be pasted.
   */
  function splitTags(value) {
    return (value || '')
      .split(',')
      .map((tag) => tag.replace(/[\u00ad\u200b-\u200f\u2060\ufeff]/g, '').trim().toLowerCase())
      .filter(Boolean);
  }

  /** Upper bound is exclusive, so the bands in taxonomy.py cannot overlap. */
  function withinBudget(product, budget) {
    const price = parseFloat(product.dataset.finderPrice);
    if (Number.isNaN(price)) return true;
    if (budget.min !== null && price < budget.min) return false;
    if (budget.max !== null && price >= budget.max) return false;
    return true;
  }

  if (!customElements.get('lagift-gift-finder')) {
    customElements.define('lagift-gift-finder', LagiftGiftFinder);
  }
})();
